'use client';

import { useEffect } from 'react';

const confettiColors = [
  '#ff6b6b',
  '#4ecdc4',
  '#45b7d1',
  '#96ceb4',
  '#ffeaa7',
  '#dda0dd',
  '#98d8c8',
];

const getSpawnDelay = () => {
  const screenWidth = window.innerWidth;
  const baseDelay = 30;
  const maxDelay = 10;

  const scaledDelay = Math.round(baseDelay + (screenWidth / 1920) * (maxDelay - baseDelay));
  return Math.max(maxDelay, scaledDelay);
};
const animationDuration = 2000;
const fallRateDurationRange = [2000, 3500];
const fallEasing = 'cubic-bezier(0.25, 0.46, 0.45, 0.94)';

const createConfettiPiece = (colors: string[]) => {
  const piece = document.createElement('div');

  const size = Math.random() * 10 + 5;
  const rotation = Math.random() * 360;
  const duration =
    Math.random() * (fallRateDurationRange[1] - fallRateDurationRange[0]) +
    fallRateDurationRange[0];

  Object.assign(piece.style, {
    position: 'fixed',
    width: `${size}px`,
    height: `${size}px`,
    backgroundColor: colors[Math.floor(Math.random() * colors.length)],
    left: `${Math.random() * 100}vw`,
    top: '-10px',
    zIndex: '9999',
    borderRadius: '0',
    pointerEvents: 'none',
  });

  const keyframes = [
    { transform: 'translateY(0px) rotate(0deg) scaleY(1)', opacity: 1 },
    {
      transform: `translateY(${window.innerHeight * 0.2}px) rotate(${rotation * 0.1}deg) scaleY(0)`,
      opacity: 1,
    },
    {
      transform: `translateY(${window.innerHeight * 0.4}px) rotate(${rotation * 0.33}deg) scaleY(1)`,
      opacity: 1,
    },
    {
      transform: `translateY(${window.innerHeight * 0.6}px) rotate(${rotation * 0.5}deg) scaleY(0)`,
      opacity: 0.8,
    },
    {
      transform: `translateY(${window.innerHeight * 0.8}px) rotate(${rotation * 0.67}deg) scaleY(1)`,
      opacity: 0.6,
    },
    {
      transform: `translateY(${window.innerHeight + 20}px) rotate(${rotation}deg) scaleY(0)`,
      opacity: 0,
    },
  ];

  const animation = piece.animate(keyframes, {
    duration,
    easing: fallEasing,
  });

  animation.onfinish = () => {
    if (piece.parentNode) {
      piece.parentNode.removeChild(piece);
    }
  };

  return piece;
};

export const PageAnimation = () => {
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const shouldAnimate = urlParams.get('animation') === 'confetti';
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (shouldAnimate && !prefersReducedMotion) {
      const spawnDelay = getSpawnDelay();
      let pieceIndex = 0;
      const maxPieces = 1000;
      const maxPiecesToSpawn = Math.min(maxPieces, Math.floor(animationDuration / spawnDelay));

      const confettiContainer = document.createElement('div');
      Object.assign(confettiContainer.style, {
        position: 'fixed',
        top: '0',
        left: '0',
        width: '100vw',
        height: '100vh',
        pointerEvents: 'none',
        zIndex: '9999',
      });
      document.body.appendChild(confettiContainer);

      const spawnInterval = setInterval(() => {
        if (pieceIndex < maxPiecesToSpawn) {
          const confettiPiece = createConfettiPiece(confettiColors);
          confettiContainer.appendChild(confettiPiece);
          pieceIndex++;
        } else {
          clearInterval(spawnInterval);
        }
      }, spawnDelay);

      return () => {
        clearInterval(spawnInterval);
        if (confettiContainer.parentNode) {
          confettiContainer.parentNode.removeChild(confettiContainer);
        }
      };
    }
  }, []);

  return null;
};
